import { useContext } from "react";
import DataSourceContext from "../../../context/DataSourceMngContext";
import { Box, IconButton, Typography, Tooltip, Stack, CircularProgress } from "@mui/material";
import {
  GradienCheckCircleIcon,
  GradienCancelIcon,
} from "../../../component/icons/icon-gradient";
import { CustomStackGrid, CustomStackRow, CustomAutocomplete, StyledPopper, StyledTextField } from "../../../styles/style";
import { GradienRemoveRedEyeIcon, GradienCalculateIcon, GradienDeleteIcon } from "../../../component/icons/icon-gradient";

export const columns_images = [
  {
    field: "id",
    headerName: "ID",
    headerAlign: "center",
    align: "center",
    sortable: false,
  },
  {
    field: "file_name",
    headerName: "File Name",
    headerAlign: "center",
    width: 300,
    align: "center",
    sortable: false,
    renderCell: (params) => {
      const isLoading = params?.row?.in_progress === true;

      return (
        <CustomStackRow direction="row" alignItems="center" justifyContent="center">
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <CustomAutocomplete
              fullWidth
              multiple
              limitTags={1}
              value={params.row.file_name || []}
              options={params.row.file_name || []}
              getOptionLabel={(option) => option}
              disableClearable
              PopperComponent={StyledPopper}
              renderInput={(params) => <StyledTextField {...params} />}
            />
          )}
        </CustomStackRow>
      )
    },
  },
  {
    field: "create_at",
    headerName: "Date",
    valueGetter: (value, row) => {
      return row.activity_date || row.create_at || null;
    },
    headerAlign: "center",
    width: 200,
    renderCell: (params) => {
      let content;
    
      if (params.row.activity_date) {
        const date = new Date(params.row.activity_date);
        const formatter = new Intl.DateTimeFormat("th-TH-u-ca-buddhist", {
          month: "long",
          year: "numeric",
        });
        content = formatter.format(date);
      } else if (params.row.create_at) {
        const date = new Date(params.row.create_at);
        content = `${date.toLocaleDateString("th-TH")} ${date.toLocaleTimeString("th-TH")}`;
      } else {
        content = "-";
      }
    
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: "14px" }} align="start">
            {content}
          </Typography>
        </CustomStackRow>
      );
    },    
  },
  {
    field: "bill_type",
    sortable: false,
    flex: 1,
    headerAlign: "center",
    align: "center",
    headerName: "Bills Type",
    renderCell: (params) => {
      const { transaction_types } = params.row;
      const bill_type = transaction_types?.map((item) => item).join(", ");
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: "14px" }} align="center">
            {bill_type}
          </Typography>
        </CustomStackRow>
      );
    },
  },
  {
    field: "model",
    sortable: false,
    flex: 1,
    headerAlign: "center",
    align: "center",
    headerName: "Model",
    renderCell: (params) => {
      const {model} = params.row
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: "14px" }} align="center">
            {model}
          </Typography>
        </CustomStackRow>
      );
    },
  },
  {
    field: "total",
    headerName: "Total",
    flex: 1,
    headerAlign: "center",
    sortable: false,
    renderCell: (params) => {
      const { file_name, total } = params.row;
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Tooltip
            title={
              <Box>
                <Typography sx={{ fontSize: "14px" }} align="start">
                  File(s)
                </Typography>
                <ul className="w-full mt-1 overflow-y-hidden">
                  {file_name.map((item, index) => (
                    <li key={`file-${index}`} className="w-full">
                      <Typography
                        sx={{ fontSize: "14px" }}
                        align="start"
                        noWrap
                      >
                        {index + 1}. {item}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            }
            enterDelay={500}
            leaveDelay={200}
          >
            <Typography
              sx={{ fontSize: "14px" }}
              align="center"
              className="cursor-pointer"
            >
              {total}
            </Typography>
          </Tooltip>
        </CustomStackRow>
      );
    },
  },
  {
    field: "total_success",
    flex: 1,
    headerAlign: "center",
    align: "center",
    sortable: false,
    renderHeader: () => (
      <Tooltip title={<Typography sx={{ fontSize: "14px" }} align="start">Successfully processed items</Typography>} arrow>
        <span>
          <GradienCheckCircleIcon sx={{ fontSize: 25 }} />
        </span>
      </Tooltip>
    ),    
    renderCell: (params) => {
      const { total_success } = params.row;
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: "14px" }} align="center">
            {total_success}
          </Typography>
        </CustomStackRow>
      );
    },
  },
  {
    field: "total_failed",
    sortable: false,
    flex: 1,
    headerAlign: "center",
    align: "center",
    renderHeader: () => (
      <Tooltip title={<Typography sx={{ fontSize: "14px" }} align="start">Needs review</Typography>} arrow>
        <span>
          <GradienCancelIcon sx={{ fontSize: 25 }} />
        </span>
      </Tooltip>
    ),    
    renderCell: (params) => {
      const { total_failed } = params.row;
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: "14px" }} align="center">
            {total_failed}
          </Typography>
        </CustomStackRow>
      );
    },
  },
  {
    field: "in_progress",
    headerName: "Status",
    sortable: false,
    width: 200,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => {
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography
            sx={{
              fontSize: "14px",
              color: params?.value ? "info.main" : "primary.main",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            {params?.value ? "Processing" : "Done"}
          </Typography>
        </CustomStackRow>
      );
    },
  },
  {
    field: "action",
    headerName: "Action",
    sortable: false,
    width: 150,
    pinned: "right",
    // headerAlign: "center",
    renderCell: (params) => <ActionCell params={params} />,
  },
];

export const columns_images_ai = [
  {
    field: "id",
    headerName: "ID",
    headerAlign: "center",
    align: "center",
    sortable: false,
  },
  {
    field: "create_at",
    headerName: "Date",
    headerAlign: "center",
    width: 400,
    renderCell: (params) => {
      const date = new Date(params.row.create_at);
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography sx={{ fontSize: "14px" }} align="start">
            {date.toLocaleDateString("th-TH")}{" "}
            {date.toLocaleTimeString("th-TH")}
          </Typography>
        </CustomStackRow>
      );
    },
  },
  {
    field: "total",
    headerName: "Total",
    flex: 1,
    headerAlign: "center",
    align: "center",
    sortable: false,
  },
  {
    field: "total_success",
    flex: 1,
    headerAlign: "center",
    align: "center",
    sortable: false,
    renderHeader: () => <GradienCheckCircleIcon fontSize="25px" />,
  },
  {
    field: "total_failed",
    sortable: false,
    flex: 1,
    headerAlign: "center",
    align: "center",
    renderHeader: () => <GradienCancelIcon fontSize="25px" />,
  },
  {
    field: "in_progress",
    headerName: "Status",
    sortable: false,
    width: 300,
    headerAlign: "center",
    align: "center",
    renderCell: (params) => {
      return (
        <CustomStackRow direction="row" spacing={0.5} alignItems="center">
          <Typography
            sx={{
              fontSize: "14px",
              color: params?.value ? "info.main" : "primary.main",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            {params?.value ? "Processing" : "Done"}
          </Typography>
        </CustomStackRow>
      );
    },
  },
];

const ActionCell = (props) => {
  const { params } = props;
  // const rowIndex = params.api.getSortedRowIds().indexOf(params.id);
  const { self_upload_type, setDialog, fetchHistory, calculateWaterMeter, deleteTransaction, setDataState, setTransactionId } = useContext(DataSourceContext);

  const handleClick = () => {
    fetchHistory(params?.row?.id);
    setDialog("device_details", true);
    setTransactionId(params?.row?.id);
  };

  const handleDelete = () => {
    setDataState((prev) => ({
      ...prev,
      warning: {
        ...prev.warning,
        open: true,
        severity: "error",
        warningTitle: "Confirm delete",
        warningText: "Are you sure you want to delete this transaction?",
        handleOkClicked: () => {
          deleteTransaction({ id: params?.row?.id });
          setDataState((prev) => ({
            ...prev,
            warning: {
              ...prev.warning,
              open: false,
            },
          }));
        },
        handleCancle: () =>
          setDataState((prev) => ({
            ...prev,
            warning: { ...prev.warning, open: false },
          })),
        okBtnText: "Delete",
      },
    }));
  };

  return (
    <CustomStackGrid direction="row" spacing={0.5}>
      {params?.row?.in_progress ? null : (
        <>
          <IconButton 
            onClick={handleClick} 
            disabled={params?.row?.id == null}
            sx={{ '&.Mui-disabled': { opacity: 0.3 }, display: params?.row?.id == null ? 'none' : 'inline-flex' }}
          >
            <GradienRemoveRedEyeIcon fontSize={20} />
          </IconButton>
          <IconButton 
            onClick={handleDelete} 
            disabled={params?.row?.id == null}
            sx={{ '&.Mui-disabled': { opacity: 0.3 }, display: params?.row?.id == null ? 'none' : 'inline-flex' }}
          >
            <GradienDeleteIcon/>
          </IconButton>
          {self_upload_type === 'water_meter' && params?.row?.is_calculate === true && (
            <IconButton 
              onClick={() => calculateWaterMeter(params?.row?.id)} 
              disabled={params?.row?.id == null}
              sx={{ '&.Mui-disabled': { opacity: 0.3 }, display: params?.row?.id == null ? 'none' : 'inline-flex' }}
            >
              <GradienCalculateIcon/>
            </IconButton>
          )}
        </>
      )}
    </CustomStackGrid>
  );
};
